import * as React from "react";
import type { maskArray } from "react-text-mask";
import MaskedInput from "react-text-mask";
import { get, isObservable, isObservableProp } from "mobx";
import { ensureObservableProperty } from "@frui.ts/helpers";
import type { IBindingProps } from "@frui.ts/views";
import { BindingComponent } from "@frui.ts/views";
import { bound } from "@frui.ts/helpers";
import { Observer } from "mobx-react-lite";
import type { FormControlProps } from "react-bootstrap";
import { Form, InputGroup } from "react-bootstrap";
import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import { getValidationMessage } from "@frui.ts/validation";

export interface BankCodeInputProps {
  placeholder?: string;
  disabled?: boolean;
  onInvalidValuePasted?: (pastedValue: string, event: any) => void;
}

export default abstract class BankCodeInput<TTarget> extends BindingComponent<
  TTarget,
  BankCodeInputProps & FormControlProps & CommonInputProps & IBindingProps<TTarget>
> {
  protected abstract mask: maskArray | ((value: string) => maskArray);
  protected valueFormatter?: (value?: string) => string | undefined;

  render() {
    return <Observer render={this.renderInner} />;
  }

  protected get value() {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string }; // the cast is a TS compiler workaround
    if (!target) {
      // tslint:disable-next-line: no-console
      console.warn("'target' prop has not been set");
      return undefined;
    }
    if (!property) {
      throw new Error("'property' prop has not been set");
    }
    if (!isObservable(target) || !isObservableProp(target, property)) {
      ensureObservableProperty(target, property, target[property]);
    }

    const value = get(target, property);

    if (this.valueFormatter) {
      return this.valueFormatter(value);
    }

    return value;
  }

  @bound
  protected renderInner() {
    const { noValidation, errorMessage, placeholder, disabled } = this.inheritedProps;
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string };
    const validationError = (noValidation !== true && errorMessage) || getValidationMessage(target, property);

    return (
      <InputGroup>
        <MaskedInput
          mask={this.mask}
          className={`form-control ${validationError ? "is-invalid" : ""} ${this.props.className}`}
          guide={false}
          showMask={false}
          value={this.value}
          autoFocus={this.props.autoFocus}
          onChange={this.handleValueChanged}
          onPaste={this.onPaste}
          placeholder={placeholder}
          disabled={disabled}
        />
        {validationError && <Form.Control.Feedback type="invalid">{validationError}</Form.Control.Feedback>}
      </InputGroup>
    );
  }

  @bound
  protected handleValueChanged(e: React.FormEvent<any>) {
    const target = e.target as HTMLInputElement;
    let newValue = target.value;
    newValue = newValue.replaceAll("-", "").replaceAll(" ", "");

    this.setValue(newValue.toUpperCase());
  }

  @bound
  protected onPaste(event: any) {
    const pastedValue = event.clipboardData.getData("text").trim();
    if (pastedValue && this.props.onInvalidValuePasted) {
      // If the onInvalidValuePasted handler sets the input data, it needs to prevent default on the paste event
      this.props.onInvalidValuePasted(pastedValue.replaceAll(" ", ""), event);
    }
  }
}
